import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import ENDPOINTS from '../../api/endPoints';
import { fetchCampaignsRequest } from '../../redux/slices/campaignsSlice';

interface TaskUpdate {
  id: string;
  accountId: string;
  restaurantId: string;
  taskType: string;
  lifecycle: string;
  stage: string;
  message: string;
  metadata: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  percentCompleted: number;
}

const Processing: React.FC = () => {
  const dispatch = useDispatch();
  const { navigate } = useRestaurantNav();
  const location = useLocation();
  const { taskId, restaurantId, requestId, useSSE } = location.state || {};
  
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState('Initializing system-generated ideas...');
  const [stage, setStage] = useState('');

  useEffect(() => {
    if (useSSE && requestId) {
      const sseUrl = ENDPOINTS.sseMonitor(requestId);
      const eventSource = new EventSource(sseUrl);

      eventSource.onopen = () => {
        console.log('SSE Connection opened for workflow monitoring');
      };

      // Listen for 'connected' event
      eventSource.addEventListener('connected', (event: any) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Connected:', data.message);
        } catch (error) {
          console.error('Error parsing connected event:', error);
        }
      });

      // Listen for 'inprogress' event - this is where progress updates come
      eventSource.addEventListener('inprogress', (event: any) => {
        
        try {
          const data = JSON.parse(event.data);
          // Update progress and message from the data
          if (data.percentCompleted !== undefined) {
            setProgress(data.percentCompleted);
          }
          if (data.message) {
            setCurrentMessage(data.message);
          }
          if (data.status) {
            setStage(data.status);
          }

          // Check if completed
          if (data.status === 'completed' || data.percentCompleted === 100) {
            
            dispatch(fetchCampaignsRequest());

            setTimeout(() => {
              eventSource.close();
              navigate('/dashboard');
            }, 1000);
          }
        } catch (error) {
          console.error('Error parsing SSE inprogress data:', error, event.data);
        }
      });

      // Listen for 'completed' event
      eventSource.addEventListener('completed', (event: any) => {
        try {
          const data = JSON.parse(event.data);
          setProgress(100);
          setCurrentMessage(data.message || 'Completed!');
          dispatch(fetchCampaignsRequest());
          setTimeout(() => {
            eventSource.close();
            navigate('/dashboard');
          }, 1000);
        } catch (error) {
          console.error('Error parsing completed event:', error);
        }
      });

      eventSource.onerror = (error) => {
        eventSource.close();
        setTimeout(() => navigate('/dashboard'), 2000);
      };

      return () => {
        eventSource.close();
      };
    }

    // Fallback to old task monitoring flow
    if (!taskId || !restaurantId) {
      navigate('/dashboard');
      return;
    }


    // Set up SSE connection using centralized endpoint
    const sseUrl = ENDPOINTS.sseTaskMonitor(taskId, restaurantId);
    const eventSource = new EventSource(sseUrl);

    eventSource.onopen = () => {
      console.log('SSE Connection opened');
    };

    // Listen for the specific 'task-update' event type
    eventSource.addEventListener('task-update', (event: any) => {
      
      try {
        const parsedData: TaskUpdate = JSON.parse(event.data);
        
        // Update state
        setProgress(parsedData.percentCompleted || 0);
        setCurrentMessage(parsedData.message || 'Processing...');
        setStage(parsedData.stage || '');

        // If completed (100%), navigate to dashboard
        if (parsedData.lifecycle === 'Completed' && parsedData.percentCompleted >= 100) {
          dispatch(fetchCampaignsRequest());
          setTimeout(() => {
            eventSource.close();
            navigate('/dashboard');
          }, 1000);
        }
      } catch (error) {
        console.error('Error parsing SSE data:', error, event.data);
      }
    });

    eventSource.onerror = (error) => {
      if (eventSource.readyState === EventSource.CLOSED) {
        console.log('SSE Connection closed');
      }
    };

    // Cleanup on unmount
    return () => {
      eventSource.close();
    };
  }, [taskId, restaurantId, navigate]);

  return (
    <div className="min-h-screen bg-[#121212] flex items-center justify-center p-6">
      <div className="max-w-2xl w-full text-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-8"
        >
          <div className="w-64 h-64 mx-auto">
            <DotLottieReact
              src="https://lottie.host/ba179149-4825-49a0-8d2b-2bff787478d0/9mFTTEa0AC.lottie"
              loop
              autoplay
            />
          </div>
        </motion.div>

        {/* Progress Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="space-y-6"
        >
          {/* Message */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              Generating Your Marketing Intelligence
            </h2>
            <p className="text-brand-primary text-base font-medium">
              {currentMessage}
            </p>
            {/* {stage && (
              <p className="text-gray-500 text-sm mt-1">
                Stage: {stage.replace(/_/g, ' ')}
              </p>
            )} */}
          </div>

          {/* Progress Bar */}
          <div className="w-full max-w-md mx-auto">
            <div className="relative">
              <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-brand-primary to-orange-600 rounded-full relative"
                >
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer" />
                </motion.div>
              </div>
              
              {/* Progress percentage */}
              <div className="flex justify-between items-center mt-2">
                <span className="text-gray-400 text-sm">Generating...</span>
                <span className="text-white text-sm font-semibold">{Math.round(progress)}%</span>
              </div>
            </div>
          </div>

        </motion.div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default Processing;
